import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '@/components/ui/select';
import adminClient from '@/lib/api/adminClient';
import { formatDateTime } from '@/lib/utils';

const STATUS_VARIANT: Record<string, 'success' | 'destructive' | 'secondary' | 'warning' | 'outline'> = {
  running: 'success',
  halted: 'destructive',
  error: 'destructive',
  paused: 'warning',
  idle: 'secondary',
  stopped: 'outline',
};

const STATUS_LABEL: Record<string, string> = {
  running: '執行中',
  halted: '熔斷',
  error: '錯誤',
  paused: '暫停',
  idle: '閒置',
  stopped: '已停止',
};

export function BotsPage() {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'bots', page, statusFilter],
    queryFn: () =>
      adminClient.get('/admin/bots', {
        params: {
          page,
          per_page: 20,
          status: statusFilter !== 'all' ? statusFilter : undefined,
        },
      }).then((r) => r.data),
  });

  // Status summary from current page
  const statusCounts = (data?.items || []).reduce((acc: Record<string, number>, bot: any) => {
    acc[bot.status] = (acc[bot.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">交易機器人（全用戶）</h1>
        <span className="text-sm text-muted-foreground">總計：{data?.total ?? 0} 台</span>
      </div>

      {/* Quick stats */}
      {data && (
        <div className="flex flex-wrap gap-2">
          {Object.entries(statusCounts).map(([status, count]) => (
            <Badge key={status} variant={STATUS_VARIANT[status] ?? 'secondary'}>
              {STATUS_LABEL[status] ?? status}: {count as number}
            </Badge>
          ))}
        </div>
      )}

      {/* Filter */}
      <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
        <SelectTrigger className="w-[150px]"><SelectValue placeholder="篩選狀態" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">所有狀態</SelectItem>
          <SelectItem value="running">執行中</SelectItem>
          <SelectItem value="idle">閒置</SelectItem>
          <SelectItem value="paused">暫停</SelectItem>
          <SelectItem value="halted">熔斷</SelectItem>
          <SelectItem value="stopped">已停止</SelectItem>
          <SelectItem value="error">錯誤</SelectItem>
        </SelectContent>
      </Select>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>用戶</TableHead>
                  <TableHead>Bot 名稱</TableHead>
                  <TableHead>商品</TableHead>
                  <TableHead>週期</TableHead>
                  <TableHead>模式</TableHead>
                  <TableHead>狀態</TableHead>
                  <TableHead>建立時間</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data?.items || []).map((bot: any) => (
                  <TableRow key={bot.id}>
                    <TableCell className="text-sm">{bot.user_email ?? `uid:${bot.user_id}`}</TableCell>
                    <TableCell className="font-medium text-sm">{bot.name}</TableCell>
                    <TableCell className="font-mono text-sm">{bot.symbol}</TableCell>
                    <TableCell className="text-sm">{bot.timeframe}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">{bot.mode}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={STATUS_VARIANT[bot.status] ?? 'secondary'}>
                        {STATUS_LABEL[bot.status] ?? bot.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{formatDateTime(bot.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {data && data.total > 20 && (
        <div className="flex items-center justify-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>上一頁</Button>
          <span className="text-sm text-muted-foreground">第 {page} 頁，共 {Math.ceil(data.total / 20)} 頁</span>
          <Button variant="outline" size="sm" disabled={page >= Math.ceil(data.total / 20)} onClick={() => setPage(page + 1)}>下一頁</Button>
        </div>
      )}
    </div>
  );
}
